import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import {
  ApdmReviewAction,
  apdmReviewIntentLabels,
} from '@euler/functions/apdm/reviewDraft';
import { useApdmDraftReview } from '@euler/functions/apdm/useApdmDraftReview';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmReplyDraftReviewSummary,
  apdmReplyOutputCharacterLimit,
} from '@euler/model/apdm';
import { CloudOff, MessageSquareText } from 'lucide-react-native';
import { memo, useState } from 'react';
import { ActivityIndicator, Pressable, TextInput, View } from 'react-native';
import {
  ApdmCard,
  ApdmEmptyList,
  ApdmListSkeleton,
  ApdmPlate,
} from '../components/ApdmSurface';
import { apdmShortTime } from './campaignDetailText';

function Button({
  label,
  tone,
  disabled,
  onPress,
}: {
  label: string;
  tone: 'primary' | 'plain' | 'quiet';
  disabled: boolean;
  onPress: () => void;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => ({
        paddingHorizontal: rpx(12),
        paddingVertical: rpx(8),
        borderRadius: rpx(10),
        borderWidth: tone === 'plain' ? 1 : 0,
        borderColor: aiColors.border,
        backgroundColor: tone === 'primary' ? aiColors.primary : aiColors.card,
        opacity: pressed || disabled ? 0.6 : 1,
      })}
    >
      <Typography.Text
        medium
        size={rpx(12)}
        color={
          tone === 'primary'
            ? '#fff'
            : tone === 'quiet'
            ? aiColors.mutedForeground
            : aiColors.foreground
        }
      >
        {label}
      </Typography.Text>
    </Pressable>
  );
}

/**
 * One reply the AI drafted: what the customer said, the draft, why, and
 * 发送 / 修改后发送 / 不发 for its reviewer.
 */
const ReviewItem = memo(
  ({
    storeId,
    draft,
    onDone,
  }: {
    storeId: number;
    draft: ApdmReplyDraftReviewSummary;
    onDone: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { state, actions } = useApdmDraftReview(storeId, draft.ref.draftId);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(draft.text);
    const detail = state.detail;
    const customer = detail?.messages.filter(
      item => item.sender === 'customer',
    );
    const submit = async (action: ApdmReviewAction) => {
      if (await actions.submit(action, text)) onDone();
    };
    return (
      <ApdmCard style={{ padding: rpx(14), gap: rpx(10) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <ApdmPlate plate={draft.licensePlateNo} />
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {apdmShortTime(draft.createdAt)}
          </Typography.Text>
        </View>
        {!detail ? (
          state.failed ? (
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              这条回复暂时读不出来。
            </Typography.Text>
          ) : (
            <ActivityIndicator color={aiColors.primary} />
          )
        ) : (
          <>
            {customer?.length ? (
              <View style={{ gap: rpx(4) }}>
                {customer.slice(-3).map(message => (
                  <View
                    key={message.id}
                    style={{
                      alignSelf: 'flex-start',
                      maxWidth: '90%',
                      paddingHorizontal: rpx(10),
                      paddingVertical: rpx(6),
                      borderRadius: rpx(12),
                      backgroundColor: aiColors.muted,
                    }}
                  >
                    <Typography.Text size={rpx(13)} color={aiColors.foreground}>
                      {message.text ?? '［非文字消息］'}
                    </Typography.Text>
                  </View>
                ))}
              </View>
            ) : null}
            <View
              style={{
                padding: rpx(10),
                borderRadius: rpx(12),
                backgroundColor: tint(aiColors.primary, 0.06),
                borderWidth: 1,
                borderColor: tint(aiColors.primary, 0.2),
                gap: rpx(4),
              }}
            >
              <Typography.Text medium size={rpx(11)} color={aiColors.primary}>
                AI 拟的回复
              </Typography.Text>
              {editing ? (
                <TextInput
                  multiline
                  value={text}
                  onChangeText={setText}
                  maxLength={apdmReplyOutputCharacterLimit}
                  style={{
                    minHeight: rpx(80),
                    fontSize: rpx(14),
                    lineHeight: rpx(20),
                    color: aiColors.foreground,
                    textAlignVertical: 'top',
                  }}
                />
              ) : (
                <Typography.Text
                  size={rpx(14)}
                  lineHeight={rpx(20)}
                  color={aiColors.foreground}
                >
                  {draft.text}
                </Typography.Text>
              )}
            </View>
            {detail.intent || detail.reason ? (
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                为什么这样回：
                {apdmReviewIntentLabels[
                  (detail.reason ??
                    detail.intent) as keyof typeof apdmReviewIntentLabels
                ] ?? '客户回复了消息'}
              </Typography.Text>
            ) : null}
            {state.failed ? (
              <Typography.Text size={rpx(12)} color={aiColors.destructive}>
                没有操作成功，这条回复可能已经过期或被处理了。
              </Typography.Text>
            ) : null}
            {draft.canReview ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  gap: rpx(8),
                }}
              >
                <Button
                  label="不发"
                  tone="quiet"
                  disabled={state.busy}
                  onPress={() => void submit(ApdmReviewAction.Discard)}
                />
                {editing ? (
                  <Button
                    label="发送修改后的"
                    tone="primary"
                    disabled={state.busy || !text.trim()}
                    onPress={() => void submit(ApdmReviewAction.EditSend)}
                  />
                ) : (
                  <>
                    <Button
                      label="修改后发送"
                      tone="plain"
                      disabled={state.busy}
                      onPress={() => setEditing(true)}
                    />
                    <Button
                      label="发送"
                      tone="primary"
                      disabled={state.busy}
                      onPress={() => void submit(ApdmReviewAction.Send)}
                    />
                  </>
                )}
              </View>
            ) : (
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                由 {draft.reviewerName ?? '负责的顾问'} 审核
              </Typography.Text>
            )}
          </>
        )}
      </ApdmCard>
    );
  },
);

export const ReviewsTab = memo(
  ({
    storeId,
    drafts,
    failed,
    onDone,
  }: {
    storeId: number;
    drafts: ApdmReplyDraftReviewSummary[] | null;
    failed: boolean;
    onDone: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    if (failed)
      return (
        <ApdmEmptyList
          icon={CloudOff}
          text="待审核的回复没有加载出来"
          onRetry={onDone}
        />
      );
    if (!drafts) return <ApdmListSkeleton />;
    if (!drafts.length)
      return <ApdmEmptyList icon={MessageSquareText} text="没有待审核的回复" />;
    return (
      <View style={{ gap: rpx(10) }}>
        {drafts.map(draft => (
          <ReviewItem
            key={draft.ref.draftId}
            storeId={storeId}
            draft={draft}
            onDone={onDone}
          />
        ))}
      </View>
    );
  },
);
